import { deviceAliases } from '../core/state';
import { can } from '../core/access';
import { FarmType } from '../core/farm-type';
import { lastSeenIndicatorHtml } from '../core/format';
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { inlineStreams, stopInlineStream, startInlineStream, renderInlinePlayer, connectIconSvg, } from '../stream/inline-stream';
import { powerIconSvg, trashSvg } from '../stream/stream-controls-shared';
import { showDeviceCommandsPopover } from './device-commands-popover';
import { showDeleteAggregatorDeviceModal } from '../farms/aggregator-devices';
// Header "Commands" button — a `.card-icon-btn` in the card's top toolbar that opens the
// device-command popover. Shared so the duo card (farms/duo-devices.ts) renders an
// identical icon in the same place instead of a hand-drawn copy in its control column.
const commandsSvg = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="1.5" y="2.5" width="13" height="11" rx="2"/><path d="m4 6 2 2-2 2M8 10h3"/></svg>';
export function commandsButtonHtml() {
    return `<button class="card-icon-btn commands-btn" title="${t('devices.commands')}">${commandsSvg}</button>`;
}
/**
 * The whole subtitle stack under a device name — ONE control, so every surface
 * (stream / duo / GeeLark cards, the clicker view's rows) renders the same lines in
 * the same order. Build it with this, refresh it with `syncDeviceSubtitlesIn`, and
 * never assemble the lines by hand: they used to be two separate builders, and the
 * refresh path re-appended one of them and silently flipped the order.
 *
 * Line 1 — the running clicker's app + task ("LemonCash · payout"), followed by the name
 * of the panel flow it plays when the run was handed one ("… · Ualá payout v3"). A device
 * may run any of its team's flows, so the pair alone doesn't say what the phone is doing.
 * Empty unless a clicker is running and at least one of the three is known.
 *
 * Line 2 — "Clicker started by <login>": the operator whose START this running clicker
 * belongs to (an automatic resume or a start from the phone's own card keeps naming
 * them, since they are still the one working the account).
 *
 * It says what it means on purpose. The earlier wording ("In work: <login>") read like
 * a pin or a lock, so operators tried to clear it with My Devices → Clear all — which
 * releases claims and never touched this line. It is purely a coordination signal — it
 * never gates control (that stays `takenByLogin`, the live stream holder). Shown for
 * our OWN run too: this line answers "whose clicker is this", and blanking it on our
 * own phones made the card look like it had lost the information rather than like the
 * answer was "you". Empty for a run started before the panel began recording starters.
 */
export function deviceSubtitlesHtml(d) {
    if (d.shiftRunning)
        return shiftSubtitlesHtml(d.shiftStartedBy ?? '');
    if (!d.clickerRunning)
        return '';
    const out = [];
    const parts = [d.clickerApp, d.clickerTask].filter((s) => !!s && s.length > 0);
    const flow = d.clickerFlow ?? '';
    if (parts.length > 0 || flow) {
        const head = esc(parts.join(' · '));
        // The flow rides on the SAME line, dimmed — the header is a fixed height and
        // already stacks two lines under the name; a third would push the card open.
        const tail = flow
            ? `${head ? ' · ' : ''}<span class="device-clicker-flow">${esc(flow)}</span>` : '';
        const title = flow ? t('devices.clicker_flow', { name: esc(flow) }) : t('devices.clicker_running');
        out.push(`<span class="device-clicker-sub" title="${title}">${head}${tail}</span>`);
    }
    const login = d.clickerStartedBy ?? '';
    if (login) {
        const text = t('devices.clicker_started_by', { login: esc(login) });
        out.push(`<span class="device-claimed-sub" title="${text}">${text}</span>`);
    }
    return out.join('\n');
}
/** A running «close shift» takes the same two lines a clicker would: what runs, and whose run it is. */
function shiftSubtitlesHtml(login) {
    const what = t('devices.shift_running');
    const out = [`<span class="device-clicker-sub" title="${what}">${what}</span>`];
    if (login) {
        const text = t('devices.shift_started_by', { login: esc(login) });
        out.push(`<span class="device-claimed-sub" title="${text}">${text}</span>`);
    }
    return out.join('\n');
}
/**
 * Repaint the subtitles inside a card's name wrap after an optimistic start/stop flip —
 * otherwise a stopped clicker keeps its green subtitle until the 20s grid poll rebuilds
 * the card. Replaces the whole stack rather than patching one line, so a refreshed card
 * is byte-identical to a freshly built one.
 */
export function syncDeviceSubtitlesIn(wrap, d) {
    wrap.querySelectorAll('.device-clicker-sub, .device-claimed-sub').forEach((el) => el.remove());
    const html = deviceSubtitlesHtml(d);
    if (html)
        wrap.insertAdjacentHTML('beforeend', html);
}
/**
 * The device-name line of a card header — one builder for every card type (stream, duo,
 * GeeLark, and the board's placeholder) so the four never drift apart.
 *
 * `farmLabel` prefixes the owning farm, muted. Only surfaces where one grid mixes farms
 * pass it — the My Devices board — so a farm's own grid reads exactly as before. It goes
 * INSIDE `.device-name` (a single line with ellipsis) rather than becoming another
 * subtitle: the header is a fixed 52px and already stacks the clicker and claim lines
 * under the name. Carrying the farm costs width, so the name also drops a couple of
 * points there — the pair has to fit on one line before ellipsis eats the device name,
 * which is the half that identifies the phone.
 */
export function deviceNameHtml(displayName, udid, farmLabel) {
    const full = farmLabel ? `${farmLabel} · ${displayName}` : displayName;
    const prefix = farmLabel ? `<span class="device-farm-prefix">${esc(farmLabel)}</span>` : '';
    return `<span class="device-name${farmLabel ? ' device-name--with-farm' : ''}" `
        + `title="${esc(full)} (${esc(udid)})">${prefix}${esc(displayName)}</span>`;
}
// Every device — host farm or aggregator phone client — streams real scrcpy video
// with touch control, so the card always shows the Connect button + inline player.
export function makeDeviceCard(d, opts = {}) {
    const aggregatorFarmId = opts.aggregatorFarmId;
    const online = d.state === 'device';
    const alias = deviceAliases.get(d.udid);
    const model = d['ro.product.model'] || '';
    const dotCls = online ? 'status-dot--green' : 'status-dot--red';
    const displayName = alias || model || d.udid;
    // Aggregator phones (phone clients) have no host agent behind them, so the command
    // popover's adb-driven actions don't apply — their cards get no Commands button.
    const commandsButton = aggregatorFarmId != null ? '' : commandsButtonHtml();
    // Delete/revoke button — admin and above only. The server route is 'admin', so
    // operators (and senior operators) never see the trash button they couldn't use.
    const deleteButton = aggregatorFarmId != null && can('device.delete')
        ? `<button class="card-icon-btn agg-device-del" title="${t('devices.delete_device')}">${trashSvg}</button>`
        : '';
    // VMOS instances are docker containers on the box's marsbox supervisor, so their
    // power state is ours to drive: the card gets a power toggle right next to Connect.
    // It is the ONE control that must stay live while the device is offline — a
    // powered-off instance still shows a card precisely so it can be switched back on.
    const vmosPower = d.farmType === FarmType.Vmos && can('device.vmosPower')
        ? `<button class="device-power-btn ${online ? 'is-on' : 'is-off'}" title="${online ? t('devices.power_off') : t('devices.power_on')}" aria-label="${online ? t('devices.power_off') : t('devices.power_on')}">${powerIconSvg}</button>`
        : '';
    const updatedHtml = lastSeenIndicatorHtml(d.lastSeen);
    const card = document.createElement('div');
    card.className = `device-card ${online ? 'online' : 'offline'}`;
    card.dataset['udid'] = d.udid;
    // NOT native-draggable — reordering is pointer-event based (see farms/device-order.ts).
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot ${dotCls}"></span>
            <span class="device-name-wrap">
                ${deviceNameHtml(displayName, d.udid, opts.farmLabel)}
                ${deviceSubtitlesHtml(d)}
            </span>
            ${updatedHtml}
            <div class="device-card__actions">
                ${commandsButton}
                ${deleteButton}
            </div>
            ${vmosPower}
            <button class="stream-btn" title="${t('devices.connect')}" aria-label="${t('devices.connect')}" ${online ? '' : 'disabled'}>${connectIconSvg}</button>
        </div>
        <div class="device-card__player"></div>
    `;
    renderInlinePlayer(card, d);
    card.querySelector('.commands-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        showDeviceCommandsPopover(e.currentTarget, d.udid, displayName);
    });
    if (aggregatorFarmId != null) {
        card.querySelector('.agg-device-del')?.addEventListener('click', (e) => {
            e.stopPropagation();
            showDeleteAggregatorDeviceModal(aggregatorFarmId, d.udid, () => card.remove());
        });
    }
    // Power on/off the VMOS instance. `docker start`/`stop` on the box is not instant
    // (Android has to boot), so the button stays disabled until the farm's fresh device
    // list lands and the grid rebuilds the card with the new state.
    card.querySelector('.device-power-btn')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        const on = !online;
        if (!on && !window.confirm(t('devices.power_off_confirm', { name: displayName })))
            return;
        btn.disabled = true;
        try {
            const res = await fetch(`/api/devices/${encodeURIComponent(d.udid)}/command`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ command: on ? 'vmos-power-on' : 'vmos-power-off' }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                alert(data.error || (on ? t('devices.power_on_failed') : t('devices.power_off_failed')));
                btn.disabled = false;
            }
        }
        catch {
            alert(t('devices.network_error'));
            btn.disabled = false;
        }
    });
    card.querySelector('.stream-btn').addEventListener('click', () => {
        if (inlineStreams.has(d.udid)) {
            stopInlineStream(d.udid);
            renderInlinePlayer(card, d);
        }
        else if (online) {
            startInlineStream(d, card);
        }
    });
    return card;
}
